<template>
  <Teleport to="body">
    <Transition name="sheet-scrim">
      <div v-if="modelValue" class="sheet-scrim" @click="close"></div>
    </Transition>
    <Transition name="sheet-slide" @after-leave="restoreFocus">
      <div
        v-if="modelValue"
        ref="sheetRef"
        class="bottom-sheet"
        role="dialog"
        aria-modal="true"
        :aria-label="title || undefined"
        tabindex="-1"
        :style="dragStyle"
        @keydown.escape.stop="close"
        @keydown.tab="trapFocus"
        @touchstart.passive="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div class="sheet-handle" aria-hidden="true"></div>
        <h2 v-if="title" class="sheet-title">{{ title }}</h2>
        <div ref="bodyRef" class="sheet-body">
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref as vueRef, computed, watch, nextTick } from "vue";

// Mobile bottom-sheet primitive: scrim + slide-up panel with a drag handle.
// Dismissal: swipe down, scrim tap, Escape, or the consumer setting v-model
// to false. Deep-navy surface in dark mode comes from the role tokens.
export default {
  name: "BottomSheet",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const sheetRef = vueRef(null);
    const bodyRef = vueRef(null);
    const dragOffset = vueRef(0);
    const isDragging = vueRef(false);
    let touchStartY = 0;
    let touchStartTime = 0;
    let previouslyFocused = null;

    const close = () => emit("update:modelValue", false);

    // While the finger is down we track it 1:1 with no transition; on release
    // the transition comes back so the snap/spring reads as motion.
    const dragStyle = computed(() => {
      if (dragOffset.value === 0 && !isDragging.value) return undefined;
      return {
        transform: `translateY(${dragOffset.value}px)`,
        transition: isDragging.value ? "none" : undefined,
      };
    });

    const onTouchStart = (event) => {
      // Only start a dismiss-drag when the scrollable body is at its top;
      // otherwise the gesture is the body scrolling.
      if (bodyRef.value && bodyRef.value.scrollTop > 0) return;
      isDragging.value = true;
      touchStartY = event.touches[0].clientY;
      touchStartTime = event.timeStamp;
    };

    const onTouchMove = (event) => {
      if (!isDragging.value) return;
      const dy = event.touches[0].clientY - touchStartY;
      if (dy > 0) {
        dragOffset.value = dy;
        // The sheet is following the finger; don't also scroll the body.
        if (event.cancelable) event.preventDefault();
      } else {
        dragOffset.value = 0;
      }
    };

    const onTouchEnd = (event) => {
      if (!isDragging.value) return;
      isDragging.value = false;
      const dy = dragOffset.value;
      const dt = Math.max(event.timeStamp - touchStartTime, 1);
      const velocity = dy / dt; // px per ms, downward positive
      const sheetHeight = sheetRef.value?.offsetHeight || 0;
      if (dy > sheetHeight * 0.25 || velocity > 0.5) {
        dragOffset.value = 0;
        close();
      } else {
        dragOffset.value = 0; // springs back via the restored transition
      }
    };

    const focusables = () =>
      Array.from(
        sheetRef.value?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((el) => !el.disabled && el.offsetParent !== null);

    const trapFocus = (event) => {
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      // The sheet container itself holds focus right after opening; wrap
      // into the items instead of letting Tab escape to the background.
      if (!items.includes(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const restoreFocus = () => {
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
      previouslyFocused = null;
    };

    watch(
      () => props.modelValue,
      async (open) => {
        if (open) {
          previouslyFocused = document.activeElement;
          dragOffset.value = 0;
          await nextTick();
          sheetRef.value?.focus();
        }
      }
    );

    return {
      sheetRef,
      bodyRef,
      dragStyle,
      close,
      trapFocus,
      restoreFocus,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    };
  },
};
</script>

<style scoped>
.sheet-scrim {
  position: fixed;
  inset: 0;
  background: var(--al-scrim, rgba(8, 26, 49, 0.45));
  z-index: 1055;
}

.bottom-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1060;
  display: flex;
  flex-direction: column;
  max-height: 85dvh;
  background: var(--al-surface);
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -8px 32px rgba(8, 26, 49, 0.24);
  padding: 0 16px max(16px, env(safe-area-inset-bottom, 0px));
  outline: none;
  transition: transform 300ms cubic-bezier(0.16, 1, 0.3, 1);
  touch-action: pan-y;
}

.sheet-handle {
  flex-shrink: 0;
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: var(--al-border);
  margin: 8px auto;
}

.sheet-title {
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.24;
  color: var(--al-text);
  margin: 4px 4px 12px;
}

.sheet-body {
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* Enter/leave: scrim fades, sheet slides. */
.sheet-scrim-enter-active {
  transition: opacity 300ms cubic-bezier(0.16, 1, 0.3, 1);
}
.sheet-scrim-leave-active {
  transition: opacity 200ms cubic-bezier(0.4, 0, 1, 1);
}
.sheet-scrim-enter-from,
.sheet-scrim-leave-to {
  opacity: 0;
}

.sheet-slide-enter-active {
  transition: transform 300ms cubic-bezier(0.16, 1, 0.3, 1);
}
.sheet-slide-leave-active {
  transition: transform 200ms cubic-bezier(0.4, 0, 1, 1);
}
.sheet-slide-enter-from,
.sheet-slide-leave-to {
  transform: translateY(100%);
}

@media (prefers-reduced-motion: reduce) {
  .bottom-sheet {
    transition: none;
  }
  .sheet-slide-enter-active,
  .sheet-slide-leave-active,
  .sheet-scrim-enter-active,
  .sheet-scrim-leave-active {
    transition: opacity 150ms ease;
  }
  .sheet-slide-enter-from,
  .sheet-slide-leave-to {
    transform: none;
    opacity: 0;
  }
}
</style>
